import { useContext } from 'react'

import { EnvironmentContext } from 'config/environment'
import { HomePageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { nowBackendDateTime } from 'modules/core/lib/dates'

import Dashboard from 'modules/dashboard/components/Dashboard'
import OneWaitlistOverlay from 'modules/upsell/components/OneWaitlistOverlay'

const HomePage = () => {
  const { company, employee, canRequestUpsell } = useCurrentSession()
  const { ignoreUpsell } = useContext(EnvironmentContext)

  const companyId = company.id

  const attendancePolicyId = employee?.attendanceEmployeesPolicyId

  const variables = useMemo(
    () => ({
      companyId,
      today: nowBackendDateTime(),
      employeeAttendancePolicyIds: attendancePolicyId ? [attendancePolicyId] : undefined,
    }),
    [companyId, attendancePolicyId]
  )

  const { data, loading } = useQuery(HomePageDocument, {
    variables,
  })

  const companyLinksVisible = !!data?.dashboard._policies.dashboardWidgetCompanyLinksVisible.can
  const timeTrackingVisible = !!data?.attendance._policies.dashboardWidgetTimeTrackingVisible.can

  const communitiesEnabled = !!data?.companies?.featureAvailabilitiesConnection.nodes?.some(
    (feature) => feature.id === 'communities_core' && feature.available
  )

  const timeTrackingManagementEnabled =
    !!data?.companies.featureAvailabilitiesConnection.nodes?.some(
      (feature) => feature.id === 'time_tracking_management' && feature.available
    )

  const enabledFeatures = data?.features?.featuresConnection?.nodes ?? []

  const pulseQuestionFeatureEnabled = !!enabledFeatures?.some(
    (feature) => feature.name === 'engagement_employee_pulse_questions'
  )

  const newInboxEnabled = !!enabledFeatures?.some((feature) => feature.name === 'enable_new_inbox')
  const newInboxManualTasksEnabled = !!enabledFeatures?.some(
    (feature) => feature.name === 'enable_new_inbox_manual_tasks'
  )

  const pulsSettingEnabled =
    data?.engagement?.settingsConnection?.edges[0]?.node?.employeePulseEnabled ?? false
  const isPulseEnabled = pulseQuestionFeatureEnabled && pulsSettingEnabled

  const pulse = data?.engagement?.employeeCurrentPulsesConnection.edges[0]?.node.pulse

  const isLoading = loading && !data

  const hasTeamStatus = data?.attendance._policies.dashboardWidgetWhoIsInVisible.can ?? false

  const canCreateGroup = data?.posts?._policies?.groupCreate?.can ?? false

  const hasData = !!data

  const oneBetaEnabled = !!data?.features?.featuresConnection?.nodes?.some(
    (feature) => feature.name === 'dev_ai_agent'
  )

  const hideInAppCommunicationsEnabled = !!enabledFeatures?.some(
    (feature) => feature.name === 'hide_in_app_communications'
  )

  const timeoffModuleEnabled = !!data?.companies?.moduleAvailabilitiesConnection.nodes?.some(
    (module) => module?.id === 'timeoff' && module?.available
  )

  const widgetConfig = useMemo(() => {
    if (!hasData) return

    return {
      shouldShowNewInboxWidget: newInboxEnabled,
      shouldShowCompanyLinksWidget: companyLinksVisible,
      shouldShowAbsencesWidget: hasTeamStatus,
      shouldShowClockInWidget:
        timeTrackingManagementEnabled && timeTrackingVisible && !!employee?.id,
    }
  }, [
    hasData,
    newInboxEnabled,
    timeTrackingManagementEnabled,
    timeTrackingVisible,
    hasTeamStatus,
    companyLinksVisible,
    employee?.id,
  ])

  return (
    <>
      {oneBetaEnabled && !ignoreUpsell && canRequestUpsell && !hideInAppCommunicationsEnabled && (
        <OneWaitlistOverlay />
      )}
      <Dashboard
        widgetConfig={widgetConfig}
        communitiesEnabled={communitiesEnabled}
        newInboxManualTasksEnabled={newInboxManualTasksEnabled}
        isLoading={isLoading}
        pulseEnabled={isPulseEnabled}
        pulse={pulse}
        canCreateGroup={canCreateGroup}
        timeoffModuleEnabled={timeoffModuleEnabled}
      />
    </>
  )
}

export default HomePage
